import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, DatePicker,
  Typography, Row, Col, Popconfirm, Tooltip, message, Alert, Tabs,
  TimePicker, InputNumber,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined,
  CalendarOutlined, TeamOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  appointmentsApi, AkimSlot, AkimAppointment, SlotCreatePayload, AppointmentStats,
} from '../../api/appointments';
import { PageHeader, StatCard, StatRow } from '../../components/shared';

const { Text } = Typography;

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ожидает', color: 'orange' },
  confirmed: { label: 'Подтверждён', color: 'blue' },
  cancelled: { label: 'Отменён', color: 'red' },
  completed: { label: 'Завершён', color: 'green' },
};

const AppointmentsList: React.FC = () => {
  const [activeTab, setActiveTab] = useState('slots');

  // Slots state
  const [slots, setSlots] = useState<AkimSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Appointments state
  const [appointments, setAppointments] = useState<AkimAppointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState<AppointmentStats | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchSlots = useCallback(async () => {
    setSlotsLoading(true);
    setError(null);
    try {
      const res = await appointmentsApi.listSlots();
      setSlots(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки слотов');
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const fetchAppointments = useCallback(async () => {
    setAppointmentsLoading(true);
    setError(null);
    try {
      const res = await appointmentsApi.listAppointments();
      setAppointments(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки записей');
    } finally {
      setAppointmentsLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await appointmentsApi.stats();
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchSlots();
    fetchAppointments();
    fetchStats();
  }, [fetchSlots, fetchAppointments, fetchStats]);

  const handleCreateSlot = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }

    setSubmitting(true);
    try {
      const payload: SlotCreatePayload = {
        date: values.date.format('YYYY-MM-DD'),
        time_start: values.time_start.format('HH:mm'),
        time_end: values.time_end.format('HH:mm'),
        max_visitors: values.max_visitors,
        note: values.note,
      };
      await appointmentsApi.createSlot(payload);
      messageApi.success('Слот создан');
      setModalOpen(false);
      form.resetFields();
      fetchSlots();
      fetchStats();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSlot = async (id: number) => {
    try {
      await appointmentsApi.deleteSlot(id);
      messageApi.success('Слот удалён');
      fetchSlots();
      fetchStats();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await appointmentsApi.updateAppointmentStatus(id, status);
      messageApi.success('Статус обновлён');
      fetchAppointments();
      fetchStats();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const slotColumns: ColumnsType<AkimSlot> = [
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      title: 'Время',
      key: 'time',
      width: 140,
      render: (_: any, r: AkimSlot) => `${r.time_start} — ${r.time_end}`,
    },
    {
      title: 'Макс. посетителей',
      dataIndex: 'max_visitors',
      key: 'max_visitors',
      width: 140,
      align: 'center',
    },
    {
      title: 'Записано',
      dataIndex: 'booked_count',
      key: 'booked_count',
      width: 100,
      align: 'center',
      render: (count: number, r: AkimSlot) => (
        <Text strong style={count >= r.max_visitors ? { color: 'var(--l-danger)' } : undefined}>
          {count} / {r.max_visitors}
        </Text>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) =>
        active ? <Tag color="green">Активный</Tag> : <Tag color="default">Закрыт</Tag>,
    },
    {
      title: 'Примечание',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (v: string) => v || <Text type="secondary">--</Text>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 80,
      render: (_: any, record: AkimSlot) => (
        <Popconfirm title="Удалить слот?" onConfirm={() => handleDeleteSlot(record.id)} okText="Да" cancelText="Нет">
          <Tooltip title="Удалить">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const appointmentColumns: ColumnsType<AkimAppointment> = [
    {
      title: 'Заявитель',
      key: 'user',
      ellipsis: true,
      render: (_: any, r: AkimAppointment) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.user_name || `Пользователь #${r.user_id}`}</Text>
          {r.user_phone && <Text type="secondary" style={{ fontSize: 12 }}>{r.user_phone}</Text>}
        </Space>
      ),
    },
    {
      title: 'Тема',
      dataIndex: 'topic',
      key: 'topic',
      ellipsis: true,
    },
    {
      title: 'Дата/время слота',
      key: 'slot',
      width: 160,
      render: (_: any, r: AkimAppointment) => (
        <Space direction="vertical" size={0}>
          <Text>{r.slot_date ? dayjs(r.slot_date).format('DD.MM.YYYY') : '--'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.slot_time_start} — {r.slot_time_end}</Text>
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => {
        const cfg = STATUS_CONFIG[status] || { label: status, color: 'default' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Дата заявки',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 160,
      render: (_: any, record: AkimAppointment) => (
        <Select
          size="small"
          value={record.status}
          style={{ width: 140 }}
          onChange={(val) => handleStatusChange(record.id, val)}
          options={[
            { value: 'pending', label: 'Ожидает' },
            { value: 'confirmed', label: 'Подтвердить' },
            { value: 'cancelled', label: 'Отменить' },
            { value: 'completed', label: 'Завершить' },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <PageHeader
        title="Запись к акиму"
        subtitle="Управление слотами и записями граждан на приём к акиму"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { form.resetFields(); setModalOpen(true); }}
           
            style={{ background: 'var(--l-accent)' }}
          >
            Создать слот
          </Button>
        }
      />

      <StatRow span={8}>
        <StatCard label="Всего слотов" value={stats?.total_slots ?? slots.length} icon={<CalendarOutlined />} />
        <StatCard
          label="Забронировано"
          value={stats?.booked_slots ?? 0}
          icon={<TeamOutlined />}
          color="var(--l-accent)"
          colorValue
        />
        <StatCard
          label="Ожидают подтверждения"
          value={stats?.pending_appointments ?? 0}
          icon={<ClockCircleOutlined />}
          color="var(--l-warn)"
          colorValue
        />
      </StatRow>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchSlots(); fetchAppointments(); fetchStats(); }}>Обновить</Button>}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ padding: '0 16px' }}
          items={[
            {
              key: 'slots',
              label: 'Слоты',
              children: (
                <Table<AkimSlot> rowKey="id" columns={slotColumns} dataSource={slots} loading={slotsLoading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Слоты не найдены' }} scroll={{ x: 900 }} />
              ),
            },
            {
              key: 'appointments',
              label: 'Записи',
              children: (
                <Table<AkimAppointment> rowKey="id" columns={appointmentColumns} dataSource={appointments} loading={appointmentsLoading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Записи не найдены' }} scroll={{ x: 900 }} />
              ),
            },
          ]}
        />
      </Card>

      {/* Create Slot Modal */}
      <Modal title="Создать слот" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreateSlot} confirmLoading={submitting} okText="Создать" cancelText="Отмена" width={500} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="date" label="Дата" rules={[{ required: true, message: 'Выберите дату' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="time_start" label="Время начала" rules={[{ required: true, message: 'Укажите время' }]}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="time_end" label="Время окончания" rules={[{ required: true, message: 'Укажите время' }]}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="max_visitors" label="Макс. посетителей" rules={[{ required: true, message: 'Укажите число' }]} initialValue={5}>
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="Примечание">
            <Input placeholder="Например: только жители мкр.3" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AppointmentsList;
